import { useEffect, useState } from 'react';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';
import Sheet from '../../shared/ui/Sheet';
import { PrimaryButton } from '../../shared/ui';
import { formatCurrency } from '../../shared/models';
import { cancelOrderWithReason } from '../../shared/services/orderService';

// Preset reasons — identical list to RestaurantCancelOrderView.swift.
const CANCELLATION_REASONS = [
  'Restaurant is too busy',
  'Item no longer available',
  'Unable to fulfill order',
  'Technical issues',
  'Other',
];

// Bottom-sheet equivalent of RestaurantCancelOrderView: order summary box,
// radio-style reason picker, custom reason when "Other", Keep / Cancel actions.
export default function CancelOrderSheet({ order, open, onClose }) {
  const [selected, setSelected] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setSelected(''); setCustomReason(''); setError(''); setBusy(false); }
  }, [open, order?.id]);

  const isOther = selected === 'Other';
  const finalReason = isOther ? customReason.trim() : selected;
  const canSubmit = !!finalReason && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      await cancelOrderWithReason(order, finalReason, 'restaurant');
      onClose();
    } catch (e) {
      setError(e.message || 'Cancellation failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onClose={busy ? () => {} : onClose} title="Cancel Order">
      <div className="px-4 pb-6 space-y-4">
        <p className="text-sm font-semibold text-gray-500 -mt-1">
          Order #{String(order?.id || '').slice(0, 8)}
        </p>

        {/* Order summary box (iOS gray card) */}
        <div className="bg-gray-100 rounded-xl p-3.5 space-y-2">
          <p className="text-sm font-bold text-gray-900">Order Details</p>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Customer:</span>
            <span className="font-medium text-gray-900">{order?.customerName || 'Customer'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Total:</span>
            <span className="font-medium text-gray-900">{formatCurrency(order?.totalAmount)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Status:</span>
            <span className="font-medium text-red-500">{order?.status}</span>
          </div>
        </div>

        {/* Reason picker */}
        <div className="space-y-1">
          <p className="text-sm font-bold text-gray-900">Why are you cancelling this order?</p>
          {CANCELLATION_REASONS.map((reason) => {
            const active = selected === reason;
            return (
              <button
                key={reason}
                onClick={() => setSelected(reason)}
                className="w-full flex items-center justify-between py-2.5 text-left"
              >
                <span className="text-sm text-gray-800">{reason}</span>
                {active
                  ? <CheckCircle2 size={20} className="text-blue-500" />
                  : <Circle size={20} className="text-gray-300" />}
              </button>
            );
          })}
          {isOther && (
            <div className="pt-1">
              <label className="text-xs font-semibold text-gray-500">Please specify:</label>
              <input
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Enter reason..."
                className="mt-1 w-full rounded-xl bg-gray-100 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-red-400/40"
              />
            </div>
          )}
        </div>

        {error && <p className="text-xs font-medium text-red-500">{error}</p>}

        <div className="flex gap-3 pt-1">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 py-3 rounded-2xl bg-gray-100 text-blue-500 font-bold text-sm active:bg-gray-200 disabled:opacity-50"
          >
            Keep Order
          </button>
          <PrimaryButton color="bg-red-500" onClick={submit} disabled={!canSubmit} className="flex-1">
            {busy ? <Loader2 size={16} className="animate-spin inline" /> : 'Cancel Order'}
          </PrimaryButton>
        </div>
      </div>
    </Sheet>
  );
}
